import type { LibraryBlock } from "../build";
import { CARD_SHADOW, ellipse, motion, photo, rect, text, WHITE } from "./helpers";

// ---------------------------------------------------------------------------
// Bilderrahmen: decorative photo frames with image placeholders (an image
// dropped onto the spot fills it and keeps its form)
// ---------------------------------------------------------------------------

const PAPER = "#fafaf9";
const TAPE = "#f1e3a4cc";

export const PHOTO_FRAMES: readonly LibraryBlock[] = [
  {
    id: "bilderrahmen-polaroid",
    title: "Polaroid mit Beschriftung",
    category: "rahmen",
    tags: ["Bilderrahmen", "Sofortbild", "Polaroid", "Foto", "Erinnerung", "Beschriftung"],
    width: 700,
    height: 840,
    anchor: "center",
    duration: 5000,
    nodes: [
      rect({
        x: 0,
        y: 0,
        w: 700,
        h: 840,
        fill: PAPER,
        shadow: CARD_SHADOW,
        name: "Papier",
        ...motion("pop", { duration: 500 }),
      }),
      photo({ x: 40, y: 40, w: 620, h: 620, radius: 0, name: "Bild", ...motion("fade") }),
      text({
        x: 40,
        y: 700,
        w: 620,
        text: "Sommer 2026",
        style: "quote",
        size: 48,
        color: "#27272a",
        align: "center",
        name: "Beschriftung",
        ...motion("fade", { delay: 300 }),
      }),
    ],
  },
  {
    id: "bilderrahmen-filmstreifen",
    title: "Filmstreifen",
    category: "rahmen",
    tags: ["Bilderrahmen", "Film", "Negativ", "Kino", "Fotos", "Serie"],
    width: 1200,
    height: 440,
    anchor: "center",
    duration: 5000,
    nodes: [
      rect({ x: 0, y: 0, w: 1200, h: 440, fill: "#111111", name: "Streifen", ...motion("fade") }),
      ...Array.from({ length: 12 }, (_, index) => [
        rect({
          x: 24 + index * 98,
          y: 20,
          w: 44,
          h: 26,
          radius: 6,
          fill: "#f5f5f4",
          name: "Loch",
          ...motion("fade"),
        }),
        rect({
          x: 24 + index * 98,
          y: 394,
          w: 44,
          h: 26,
          radius: 6,
          fill: "#f5f5f4",
          name: "Loch",
          ...motion("fade"),
        }),
      ]).flat(),
      ...[0, 1, 2].map((index) =>
        photo({
          x: 30 + index * 390,
          y: 70,
          w: 360,
          h: 300,
          radius: 0,
          name: `Bild ${index + 1}`,
          ...motion("fade", { delay: 150 * index }),
        }),
      ),
    ],
  },
  {
    id: "bilderrahmen-klebeband",
    title: "Foto mit Klebeband",
    category: "rahmen",
    tags: ["Bilderrahmen", "Tape", "Klebestreifen", "Scrapbook", "Pinnwand", "Foto"],
    width: 760,
    height: 620,
    anchor: "center",
    duration: 5000,
    nodes: [
      rect({
        x: 30,
        y: 30,
        w: 700,
        h: 560,
        fill: WHITE,
        shadow: CARD_SHADOW,
        name: "Rand",
        ...motion("pop", { duration: 500 }),
      }),
      photo({ x: 56, y: 56, w: 648, h: 508, radius: 0, name: "Bild", ...motion("fade") }),
      rect({
        x: -40,
        y: 32,
        w: 200,
        h: 56,
        rotation: -45,
        fill: TAPE,
        name: "Klebeband",
        ...motion("fade", { delay: 300 }),
      }),
      rect({
        x: 600,
        y: 532,
        w: 200,
        h: 56,
        rotation: -45,
        fill: TAPE,
        name: "Klebeband",
        ...motion("fade", { delay: 400 }),
      }),
    ],
  },
  {
    id: "bilderrahmen-karte",
    title: "Fotokarte mit Schatten",
    category: "rahmen",
    tags: ["Bilderrahmen", "Karte", "Card", "Schatten", "Foto", "Beitrag"],
    width: 720,
    height: 900,
    anchor: "center",
    duration: 5000,
    nodes: [
      rect({
        x: 0,
        y: 0,
        w: 720,
        h: 900,
        fill: WHITE,
        radius: 32,
        shadow: CARD_SHADOW,
        name: "Karte",
        ...motion("slide-up", { duration: 500 }),
      }),
      photo({ x: 24, y: 24, w: 672, h: 520, radius: 20, name: "Bild", ...motion("fade") }),
      text({
        x: 48,
        y: 584,
        w: 624,
        text: "Titel der Karte",
        style: "heading-2",
        size: 52,
        color: "#18181b",
        name: "Titel",
        ...motion("fade", { delay: 200 }),
      }),
      text({
        x: 48,
        y: 670,
        w: 624,
        text: "Eine kurze Beschreibung zum Bild, zwei Zeilen passen gut.",
        style: "body",
        size: 30,
        color: "#52525b",
        name: "Beschreibung",
        ...motion("fade", { delay: 300 }),
      }),
    ],
  },
  {
    id: "bilderrahmen-kreis-ring",
    title: "Kreis mit Ring",
    category: "rahmen",
    tags: ["Bilderrahmen", "Kreis", "Profilbild", "Porträt", "Avatar", "Ring"],
    width: 800,
    height: 800,
    anchor: "center",
    duration: 5000,
    nodes: [
      ellipse({
        x: 0,
        y: 0,
        w: 800,
        h: 800,
        fill: "#00000000",
        stroke: { color: "accent", width: 12 },
        name: "Ring",
        ...motion("pop", { duration: 500 }),
      }),
      photo({
        x: 44,
        y: 44,
        w: 712,
        h: 712,
        mask: { shape: "circle" },
        name: "Bild",
        ...motion("zoom"),
      }),
    ],
  },
  {
    id: "bilderrahmen-bogen",
    title: "Bogenfenster mit Sonne",
    category: "rahmen",
    tags: ["Bilderrahmen", "Bogen", "Fenster", "Arch", "Boho", "Foto"],
    width: 720,
    height: 940,
    anchor: "center",
    duration: 5000,
    nodes: [
      ellipse({
        x: 520,
        y: 0,
        w: 200,
        h: 200,
        fill: "accent",
        name: "Sonne",
        ...motion("pop", { delay: 300 }),
      }),
      photo({
        x: 0,
        y: 60,
        w: 640,
        h: 880,
        mask: { shape: "arch" },
        name: "Bild",
        ...motion("slide-up"),
      }),
    ],
  },
  {
    id: "bilderrahmen-blob",
    title: "Organische Form",
    category: "rahmen",
    tags: ["Bilderrahmen", "Blob", "organisch", "Welle", "verspielt", "Foto"],
    width: 860,
    height: 800,
    anchor: "center",
    duration: 5000,
    nodes: [
      ellipse({
        x: 80,
        y: 60,
        w: 780,
        h: 740,
        fill: "accent",
        opacity: 0.35,
        name: "Fleck",
        ...motion("pop"),
      }),
      photo({
        x: 0,
        y: 0,
        w: 800,
        h: 760,
        mask: { shape: "blob1" },
        name: "Bild",
        ...motion("zoom", { delay: 150 }),
      }),
    ],
  },
];
